import React, { useState, useEffect } from "react";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Button, Input, Table,
  Badge, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Form,
  FormGroup, Label, FormFeedback,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  getPlantas, createPlanta, updatePlanta, deletePlanta,
} from "../../helpers/backend_helper";

const Plantas = () => {
  const [plantas, setPlantas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPlanta, setCurrentPlanta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [soloActivas, setSoloActivas] = useState(true);

  const [formData, setFormData] = useState({
    estado: "", nombre: "", direccion: "",
    coordenadas: ["", ""],
    contactos: [{ nombre: "", telefono: "" }],
    activo: true,
  });

  const [formErrors, setFormErrors] = useState({});

  const estadosVenezolanos = [
    "Amazonas", "Anzoátegui", "Apure", "Aragua", "Barinas", "Bolívar", "Carabobo",
    "Cojedes", "Delta Amacuro", "Distrito Capital", "Falcón", "Guárico", "Lara", "La Guaira", "Mérida", "Miranda",
    "Monagas", "Nueva Esparta", "Portuguesa", "Sucre", "Táchira", "Trujillo",
    "Vargas", "Yaracuy", "Zulia"
  ];

  const cargarPlantas = async () => {
    setLoading(true);
    try {
      const params = { solo_activas: soloActivas, ...(filtroEstado && { estado: filtroEstado }) };
      //console.log("🔍 Cargando plantas con params:", params);
      const response = await getPlantas(params);
      //console.log("📦 Respuesta del backend:", response);
      //console.log("📋 response.data.data:", response?.data?.data);
      //console.log("✅ Es array?", Array.isArray(response?.data?.data));
      if (response?.data?.data && Array.isArray(response.data.data)) {
        //console.log("✅ Seteando plantas:", response.data.data.length, "plantas");
        setPlantas(response.data.data);
      } else {
        //console.log("❌ No hay data válida, seteando array vacío");
        setPlantas([]);
      }
    } catch (error) {
      console.error("❌ Error al cargar plantas:", error);
      toast.error("Error al cargar plantas");
      setPlantas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarPlantas(); }, [filtroEstado, soloActivas]);

  useEffect(() => {
    //console.log("🌱 Estado de plantas actualizado:", plantas);
    //console.log("🌱 Cantidad de plantas:", plantas?.length);
    //console.log("🌱 Es array?", Array.isArray(plantas));
  }, [plantas]);

  const handleNuevaPlanta = () => {
    setEditMode(false);
    setCurrentPlanta(null);
    setFormData({
      estado: "", nombre: "", direccion: "", coordenadas: ["", ""],
      contactos: [{ nombre: "", telefono: "" }], activo: true,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleEditarPlanta = (planta) => {
    setEditMode(true);
    setCurrentPlanta(planta);
    
    // Asegurar que contactos siempre sea un array válido
    let contactos = [{ nombre: "", telefono: "" }];
    if (Array.isArray(planta.contactos) && planta.contactos.length > 0) {
      contactos = planta.contactos;
    }
    
    setFormData({
      estado: planta.estado, 
      nombre: planta.nombre, 
      direccion: planta.direccion,
      coordenadas: planta.coordenadas || ["", ""],
      contactos: contactos,
      activo: planta.activo,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const validarFormulario = () => {
    const errors = {};
    if (!formData.estado.trim()) errors.estado = "El estado es requerido";
    if (!formData.nombre.trim()) errors.nombre = "El nombre es requerido";
    if (!formData.direccion.trim()) errors.direccion = "La dirección es requerida";

    const lat = parseFloat(formData.coordenadas[0]);
    const lng = parseFloat(formData.coordenadas[1]);
    if (isNaN(lat) || lat < -90 || lat > 90) errors.coordenadas = "Latitud inválida";
    if (isNaN(lng) || lng < -180 || lng > 180) {
      errors.coordenadas = errors.coordenadas ? errors.coordenadas + " y longitud inválida" : "Longitud inválida";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGuardarPlanta = async () => {
    if (!validarFormulario()) {
      toast.error("Corrija los errores en el formulario");
      return;
    }

    setSubmitting(true);
    try {
      const contactosValidos = formData.contactos.filter(c => c.nombre.trim() && c.telefono.trim());
      const plantaData = {
        estado: formData.estado, nombre: formData.nombre, direccion: formData.direccion,
        coordenadas: [parseFloat(formData.coordenadas[0]), parseFloat(formData.coordenadas[1])],
        contactos: contactosValidos, activo: formData.activo,
      };

      if (editMode && currentPlanta) {
        await updatePlanta(currentPlanta.id, plantaData);
        toast.success("✓ Planta actualizada");
      } else {
        await createPlanta(plantaData);
        toast.success("✓ Planta creada");
      }

      setModalOpen(false);
      await cargarPlantas();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEliminarPlanta = async (planta) => {
    if (!window.confirm(`¿Eliminar "${planta.nombre}"?`)) return;
    try {
      await deletePlanta(planta.id);
      toast.success("✓ Planta eliminada");
      await cargarPlantas();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleAgregarContacto = () => {
    setFormData({ ...formData, contactos: [...formData.contactos, { nombre: "", telefono: "" }] });
  };

  const handleRemoverContacto = (index) => {
    const nuevos = formData.contactos.filter((_, i) => i !== index);
    setFormData({ ...formData, contactos: nuevos.length > 0 ? nuevos : [{ nombre: "", telefono: "" }] });
  };

  const handleContactoChange = (index, field, value) => {
    const nuevos = [...formData.contactos];
    nuevos[index][field] = value;
    setFormData({ ...formData, contactos: nuevos });
  };

  document.title = "Plantas | Farme";

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="ri-building-line me-2 text-primary fs-20"></i>
                  <h5 className="card-title mb-0">Gestión de Plantas</h5>
                </div>
                <Button color="primary" onClick={handleNuevaPlanta}>
                  <i className="ri-add-line me-1"></i>Nueva Planta
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label>Filtrar por Estado</Label>
                      <Input type="select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        {estadosVenezolanos.map(e => <option key={e} value={e}>{e}</option>)}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <FormGroup check>
                      <Input type="checkbox" id="soloActivas" checked={soloActivas}
                        onChange={(e) => setSoloActivas(e.target.checked)} />
                      <Label check for="soloActivas">Solo activas</Label>
                    </FormGroup>
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2">Cargando...</p></div>
                ) : plantas.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="ri-building-line fs-1 text-muted"></i>
                    <p className="mt-2 text-muted">No hay plantas</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Estado</th><th>Nombre</th><th>Dirección</th>
                          <th>Contactos</th><th>Estado</th><th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(plantas) && plantas.map(p => (
                          <tr key={p.id}>
                            <td><Badge color="info" className="badge-soft-info">{p.estado}</Badge></td>
                            <td><strong>{p.nombre}</strong></td>
                            <td className="text-break" style={{ maxWidth: "300px" }}><small>{p.direccion}</small></td>
                            <td>
                              {Array.isArray(p.contactos) && p.contactos.length > 0 ? (
                                <div>{p.contactos.map((c, i) => (
                                  <div key={i} className="mb-1">
                                    <small><i className="ri-user-line me-1"></i>{c.nombre}<br />
                                      <i className="ri-phone-line me-1"></i>{c.telefono}</small>
                                  </div>
                                ))}</div>
                              ) : (
                                <small className="text-muted">Sin contactos</small>
                              )}
                            </td>
                            <td>{p.activo ? <Badge color="success">Activa</Badge> : <Badge color="secondary">Inactiva</Badge>}</td>
                            <td className="text-center">
                              <div className="d-flex gap-2 justify-content-center">
                                <Button color="primary" size="sm" onClick={() => handleEditarPlanta(p)}>
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button color="danger" size="sm" onClick={() => handleEliminarPlanta(p)}>
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          <i className={`${editMode ? 'ri-edit-line' : 'ri-add-line'} me-2`}></i>
          {editMode ? "Editar Planta" : "Nueva Planta"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Estado <span className="text-danger">*</span></Label>
                  <Input type="select" value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    invalid={!!formErrors.estado}>
                    <option value="">Seleccione...</option>
                    {estadosVenezolanos.map(e => <option key={e} value={e}>{e}</option>)}
                  </Input>
                  <FormFeedback>{formErrors.estado}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Nombre <span className="text-danger">*</span></Label>
                  <Input type="text" value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    invalid={!!formErrors.nombre} placeholder="Ej: Zona Industrial" />
                  <FormFeedback>{formErrors.nombre}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Dirección <span className="text-danger">*</span></Label>
              <Input type="textarea" rows="2" value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                invalid={!!formErrors.direccion} />
              <FormFeedback>{formErrors.direccion}</FormFeedback>
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Latitud <span className="text-danger">*</span></Label>
                  <Input type="number" step="any" value={formData.coordenadas[0]}
                    onChange={(e) => setFormData({ ...formData, coordenadas: [e.target.value, formData.coordenadas[1]] })}
                    invalid={!!formErrors.coordenadas} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Longitud <span className="text-danger">*</span></Label>
                  <Input type="number" step="any" value={formData.coordenadas[1]}
                    onChange={(e) => setFormData({ ...formData, coordenadas: [formData.coordenadas[0], e.target.value] })}
                    invalid={!!formErrors.coordenadas} />
                  {formErrors.coordenadas && <div className="invalid-feedback d-block">{formErrors.coordenadas}</div>}
                </FormGroup>
              </Col>
            </Row>

            <hr />
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0">Contactos</h6>
              <Button color="success" size="sm" onClick={handleAgregarContacto}>
                <i className="ri-add-line me-1"></i>Agregar
              </Button>
            </div>

            {Array.isArray(formData.contactos) && formData.contactos.map((c, i) => (
              <Row key={i} className="mb-2">
                <Col md={5}>
                  <Input type="text" value={c.nombre}
                    onChange={(e) => handleContactoChange(i, 'nombre', e.target.value)}
                    placeholder="Nombre" />
                </Col>
                <Col md={5}>
                  <Input type="text" value={c.telefono}
                    onChange={(e) => handleContactoChange(i, 'telefono', e.target.value)}
                    placeholder="+58 414-5916338" />
                </Col>
                <Col md={2}>
                  {formData.contactos.length > 1 && (
                    <Button color="danger" size="sm" onClick={() => handleRemoverContacto(i)} className="w-100">
                      <i className="ri-delete-bin-line"></i>
                    </Button>
                  )}
                </Col>
              </Row>
            ))}

            <FormGroup check className="mt-3">
              <Input type="checkbox" id="activo" checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })} />
              <Label check for="activo">Planta activa</Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>Cancelar</Button>
          <Button color="primary" onClick={handleGuardarPlanta} disabled={submitting}>
            {submitting ? <><Spinner size="sm" className="me-1" />Guardando...</> : <><i className="ri-save-line me-1"></i>Guardar</>}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Plantas;
